import { ParsedPayloadPanel } from '@/features/SalesforceIngest/components/ParsedPayloadPanel'
import type { PayloadParseResult } from '@/features/SalesforceIngest/lib/parsePayloadBlock'
import {
  Box,
  Button,
  Collapsible,
  Field,
  HStack,
  Heading,
  Textarea,
} from '@chakra-ui/react'
import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

type Props = {
  raw: string
  onRawChange: (raw: string) => void
  parsed: PayloadParseResult
  /** i18n key suffix under `payload.error`, set on a blocked submit. */
  error?: string
  /** False once a paste has parsed, unless the operator reopened the box. */
  isPasteOpen: boolean
  onReplacePaste: () => void
  /**
   * Fired when a paste replaces the whole box, which is the "Replace payload
   * block" flow completing.
   */
  onBlockPasted: () => void
}

/**
 * True when the paste about to land replaces the entire contents of the box,
 * rather than editing something already in it.
 *
 * An empty box reads as a selection of `0, 0` over a zero length value, which
 * is correctly a whole-block replacement: that is the first paste into a
 * fresh form. A selection that cannot be read counts as one too, so a host
 * without selection support falls back to the collapse behaviour that
 * shipped rather than to a box that never collapses again.
 */
function replacesWholeBlock(textarea: HTMLTextAreaElement): boolean {
  // `selectionStart` and `selectionEnd` are non-nullable on a *textarea*: only
  // `HTMLInputElement` types them nullable, for the input types that report no
  // selection. So the guard below is not about a value the spec allows, it is
  // about a host that does not honour the type, and the value one of those
  // hands back is `undefined` rather than `null`. Checked with `typeof`
  // because a `null` comparison would miss that and fall through to the
  // length test, which answers `false` and suppresses the collapse for good.
  const { selectionStart, selectionEnd } = textarea
  if (typeof selectionStart !== 'number' || typeof selectionEnd !== 'number') {
    return true
  }

  return selectionStart === 0 && selectionEnd === textarea.value.length
}

/**
 * The paste and its echo, in one slot with two states.
 *
 * The textarea is transport; the panel is the safety check the operator makes
 * before triggering up to 100 licence creations. Leaving eight rows of
 * monospace above that check pushes it down the page and works against the
 * only reason it exists, so once the paste has parsed the textarea collapses
 * and the summary takes its place. That collapse is not unconditional: while
 * the operator is editing in a box they reopened themselves, `isPasteOpen`
 * stays true and both the box and the summary are on screen together
 * (APP-2491).
 *
 * "Replace paste" rather than "Edit": re-pasting is the real recovery when
 * the wrong case was grabbed, and hand-editing raw JSON in a small box is not
 * a path worth signposting. Reopening keeps the existing text.
 *
 * The whole-block paste is reported upward rather than acted on here. This
 * section does not own the collapse (it is told about it through
 * `isPasteOpen`), and the rule the paste feeds is about which case is in the
 * box, which is `usePastedDecisions`'s business. All this component knows is
 * what the DOM selection says.
 */
export function PayloadBlockSection({
  raw,
  onRawChange,
  parsed,
  error,
  isPasteOpen,
  onReplacePaste,
  onBlockPasted,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const heading = String(t('salesforceIngest.parsed.heading'))
  // Distinct from the textarea's own "Payload block" label: this names the
  // parsed result, and two regions sharing one name helps nobody.
  const regionLabel = String(t('salesforceIngest.parsed.regionLabel'))

  // Only while the block is collapsed. With it open, the textarea's own
  // `Field.Label` already reads "Payload block", and showing both would
  // print the same short phrase twice above one field.
  const showHeading = parsed.ok && !isPasteOpen

  // "Replace payload block" unmounts itself: the heading it sits in is hidden
  // the moment the box opens. Without this the operator who pressed it is
  // dropped to `<body>`, with no focus and nothing announced. Focus moves to
  // the box they just asked for instead. Only on the closed-to-open
  // transition, never on mount, where the box is already open and stealing
  // focus would be rude.
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wasPasteOpen = useRef(isPasteOpen)
  useEffect(() => {
    if (isPasteOpen && !wasPasteOpen.current) {
      textareaRef.current?.focus()
    }
    wasPasteOpen.current = isPasteOpen
  }, [isPasteOpen])

  return (
    <Box>
      {showHeading ? (
        <HStack justify="space-between" align="center" mb={2}>
          <Heading as="h3" fontSize="md" m={0}>
            {heading}
          </Heading>
          <Button size="xs" variant="outline" onClick={onReplacePaste}>
            {String(t('salesforceIngest.parsed.replacePayloadBlock'))}
          </Button>
        </HStack>
      ) : null}

      <Collapsible.Root open={isPasteOpen}>
        <Collapsible.Content>
          <Field.Root required invalid={Boolean(error)}>
            <Field.Label>
              {String(t('salesforceIngest.form.payload.label'))}
              <Field.RequiredIndicator />
            </Field.Label>
            <Textarea
              ref={textareaRef}
              value={raw}
              onChange={(event) => onRawChange(event.target.value)}
              // Read on `paste` rather than after the change, because by the
              // time the value has moved the selection it replaced is gone.
              onPaste={(event) => {
                if (replacesWholeBlock(event.currentTarget)) {
                  onBlockPasted()
                }
              }}
              placeholder={String(
                t('salesforceIngest.form.payload.placeholder'),
              )}
              fontFamily="mono"
              fontSize="sm"
              rows={8}
            />
            {error ? (
              <Field.ErrorText>
                {String(t(`salesforceIngest.form.payload.error.${error}`))}
              </Field.ErrorText>
            ) : (
              <Field.HelperText>
                {String(t('salesforceIngest.form.payload.helper'))}
              </Field.HelperText>
            )}
          </Field.Root>
        </Collapsible.Content>
      </Collapsible.Root>

      {/* Polite, not assertive: a parse succeeding is worth announcing, but
          it happens mid-paste and must not interrupt. Without this the parse
          result is visible to sighted users and silent to everyone else. */}
      {/* An `<output>` is what makes the name land. ARIA 1.2 prohibits
          `aria-label` on an element whose implicit role is `generic`, and
          browsers drop it, so a bare `<Box aria-live aria-label>` announced
          the panel with no name at all. `output` has an implicit `status`
          role, which takes a name (and is the accurate one: this is the
          result of parsing what was pasted). `aria-live` stays explicit for
          the older screen readers that key off the attribute. */}
      {/* The summary's last row carries a divider, which butts straight up
          against the first question below it. The stack gap alone does not
          read as a separation once that rule is there. */}
      <Box
        as="output"
        display="block"
        aria-live="polite"
        aria-label={regionLabel}
        mt={parsed.ok && isPasteOpen ? 4 : 0}
        pb={parsed.ok ? 3 : 0}
      >
        {parsed.ok ? (
          <ParsedPayloadPanel
            payload={parsed.payload}
            licenceTotal={parsed.licenceTotal}
          />
        ) : null}
      </Box>
    </Box>
  )
}
